import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAssociates, createAssociate, updateAssociate, resetAssociatePassword, deactivateAssociate, getProjects, setReportsTo } from '../utils/api';
import { Plus, Pencil, UserX, KeyRound, UserCheck, GitBranch } from 'lucide-react';
import toast from 'react-hot-toast';
import { ASSIGNABLE_ROLES, roleLabel } from '../utils/roles';

const ROLE_BADGE_STYLES = {
  clm: { bg: '#EDE7F6', color: '#5E35B1' },
  bdo: { bg: 'var(--blue-light)', color: '#185FA5' },
  sales_rep: { bg: '#FFF3E0', color: '#B36A00' },
  crm: { bg: '#FCE4EC', color: '#AD1457' },
  associate: { bg: 'var(--green-light)', color: 'var(--green-dark)' },
  leader: { bg: 'var(--blue-light)', color: '#185FA5' }, // legacy
};

function ChangeLeaderModal({ associate, allAssociates, onClose }) {
  const qc = useQueryClient();
  // Pre-select current leader; exclude self
  const [selectedLeader, setSelectedLeader] = useState(associate.added_by || '');
  const options = (allAssociates || []).filter(a => a.id !== associate.id);

  const mutation = useMutation({
    mutationFn: () => setReportsTo(associate.id, selectedLeader || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['associates'] });
      qc.invalidateQueries({ queryKey: ['full-hierarchy'] });
      toast.success('Leader updated successfully');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head">
          <h3>Change Leader — {associate.name}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--blue-light)', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#185FA5', marginBottom: 4 }}>
            Currently reports to: <strong>{associate.added_by_name || 'No one (Top Level)'}</strong>
          </div>
          <div className="form-group">
            <label className="form-label">Assign New Leader / Reports To</label>
            <select className="form-select" value={selectedLeader} onChange={e => setSelectedLeader(e.target.value)}>
              <option value="">— Top Level (no leader) —</option>
              {options.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} ({roleLabel(p.role)})
                </option>
              ))}
            </select>
            <p style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 4 }}>
              Select "Top Level" to make this person a root node with no leader above them.
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-green btn-sm" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Saving...' : 'Update Leader'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssociateModal({ associate, projects, allAssociates, onClose }) {
  const qc = useQueryClient();
  const isEdit = !!associate?.id;
  const [name, setName] = useState(associate?.name || '');
  const [email, setEmail] = useState(associate?.email || '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(associate?.role || 'associate');
  const [addedBy, setAddedBy] = useState(associate?.added_by || '');
  const [selectedProjects, setSelectedProjects] = useState(
    associate?.projects?.map(p => p.id) || []
  );

  const toggleProject = (id) => {
    setSelectedProjects(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const mutation = useMutation({
    mutationFn: isEdit
      ? () => updateAssociate(associate.id, { name, role, project_ids: selectedProjects })
      : () => createAssociate({ name, email, password, role, project_ids: selectedProjects, added_by: addedBy || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['associates'] });
      qc.invalidateQueries({ queryKey: ['full-hierarchy'] });
      toast.success(isEdit ? 'Updated' : `${roleLabel(role)} created`);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name) return toast.error('Name required');
    if (!isEdit && (!email || !password)) return toast.error('Email and password required');
    if (!isEdit && password.length < 8) return toast.error('Password must be at least 8 characters');
    mutation.mutate();
  };

  // Exclude self from "reports to" options
  const reportsToOptions = (allAssociates || []).filter(a => a.id !== associate?.id);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-head">
          <h3>{isEdit ? `Edit ${associate.name}` : 'Add New Team Member'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Rakesh Sharma" required />
              </div>
              <div className="form-group">
                <label className="form-label">Role *</label>
                <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
                  {ASSIGNABLE_ROLES.map(r => (
                    <option key={r} value={r}>{roleLabel(r)}</option>
                  ))}
                  {/* Legacy role: only shown if this person already has it, so it doesn't disappear from their record */}
                  {associate?.role === 'leader' && <option value="leader">{roleLabel('leader')} (legacy)</option>}
                </select>
              </div>
            </div>

            {!isEdit && (
              <>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@srgrealestate.com" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password * (min 8 chars)</label>
                  <input type="password" className="form-input" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
                </div>
                <div className="form-group">
                  <label className="form-label">Reports To (optional)</label>
                  <select className="form-select" value={addedBy} onChange={e => setAddedBy(e.target.value)}>
                    <option value="">— Top-level (no manager) —</option>
                    {reportsToOptions.map(p => (
                      <option key={p.id} value={p.id}>{p.name} ({roleLabel(p.role)})</option>
                    ))}
                  </select>
                  <p style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
                    Places this person under another team member in the hierarchy tree.
                  </p>
                </div>
              </>
            )}

            <div className="form-group">
              <label className="form-label">Assigned Projects</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto', border: '1.5px solid var(--border)', borderRadius: 8, padding: 12 }}>
                {projects.length === 0 ? (
                  <p style={{ fontSize: 12, color: 'var(--text3)' }}>No projects available.</p>
                ) : projects.map(p => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13.5 }}>
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(p.id)}
                      onChange={() => toggleProject(p.id)}
                      style={{ width: 16, height: 16, accentColor: 'var(--green)' }}
                    />
                    {p.name}
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>({p.type})</span>
                  </label>
                ))}
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
                This person will only see live inventory for the projects checked above.
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-green btn-sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ associate, onClose }) {
  const [newPassword, setNewPassword] = useState('');
  const mutation = useMutation({
    mutationFn: () => resetAssociatePassword(associate.id, newPassword),
    onSuccess: () => { toast.success('Password reset'); onClose(); },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error('Password must be at least 8 characters');
    mutation.mutate();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head"><h3>Reset Password — {associate.name}</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">New Password (min 8 chars)</label>
              <input type="password" className="form-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-green btn-sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminAssociates() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null);
  const [pwdModal, setPwdModal] = useState(null);
  const [leaderModal, setLeaderModal] = useState(null);

  const { data: associates = [], isLoading } = useQuery({ queryKey: ['associates'], queryFn: getAssociates });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: getProjects });

  const deactivateMutation = useMutation({
    mutationFn: deactivateAssociate,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['associates'] }); toast.success('Associate deactivated'); },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id) => updateAssociate(id, { is_active: true }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['associates'] }); toast.success('Associate reactivated'); },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleDeactivate = (a) => {
    if (!window.confirm(`Deactivate ${a.name}? They will no longer be able to log in.`)) return;
    deactivateMutation.mutate(a.id);
  };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Sales Team</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 2 }}>
            Read-only inventory access and reporting hierarchy for your sales team
          </p>
        </div>
        <button className="btn btn-green" onClick={() => setModal('new')}>
          <Plus size={16} /> Add Team Member
        </button>
      </div>

      <div className="card">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : associates.length === 0 ? (
          <div className="empty-state"><p>No team members yet. Add a CLM, BDO, Sales Representative, or Associate to give read-only inventory access.</p></div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Reports To</th>
                  <th>Assigned Projects</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {associates.map(a => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 600 }}>{a.name}</td>
                    <td>
                      <span style={{
                        fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                        background: (ROLE_BADGE_STYLES[a.role] || ROLE_BADGE_STYLES.associate).bg,
                        color: (ROLE_BADGE_STYLES[a.role] || ROLE_BADGE_STYLES.associate).color,
                      }}>
                        {roleLabel(a.role)}
                      </span>
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--text2)' }}>{a.email}</td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>{a.added_by_name || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 240 }}>
                        {a.projects.length === 0 ? (
                          <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>None assigned</span>
                        ) : a.projects.map(p => (
                          <span key={p.id} style={{ fontSize: 10.5, background: 'var(--green-light)', color: 'var(--green-dark)', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                            {p.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${a.is_active ? 'badge-available' : 'badge-nfs'}`}>
                        {a.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ fontSize: 11.5, color: 'var(--text3)' }}>
                      {a.last_login ? new Date(a.last_login).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'Never'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-outline btn-sm btn-icon" onClick={() => setModal(a)} title="Edit">
                          <Pencil size={13} />
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" onClick={() => setPwdModal(a)} title="Reset Password">
                          <KeyRound size={13} />
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" onClick={() => setLeaderModal(a)} title="Change Leader">
                          <GitBranch size={13} />
                        </button>
                        {a.is_active ? (
                          <button className="btn btn-sm btn-icon" onClick={() => handleDeactivate(a)} title="Deactivate"
                            style={{ background: 'var(--red-light)', color: 'var(--red)', border: 'none' }}>
                            <UserX size={13} />
                          </button>
                        ) : (
                          <button className="btn btn-sm btn-icon" onClick={() => reactivateMutation.mutate(a.id)} title="Reactivate"
                            style={{ background: 'var(--green-light)', color: 'var(--green-dark)', border: 'none' }}>
                            <UserCheck size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <AssociateModal
          associate={modal === 'new' ? null : modal}
          projects={projects}
          allAssociates={associates}
          onClose={() => setModal(null)}
        />
      )}
      {pwdModal && <ResetPasswordModal associate={pwdModal} onClose={() => setPwdModal(null)} />}
      {leaderModal && <ChangeLeaderModal associate={leaderModal} allAssociates={associates} onClose={() => setLeaderModal(null)} />}
    </div>
  );
}
